// src/pages/ConsultarHorarios.jsx
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../styles/styles.css';
import '../styles/formularios.css';
import '@fortawesome/fontawesome-free/css/all.min.css';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

import logoInstituto from '../components/images/logo.png';
import logoSEP from '../components/images/SEP_Logo_2019.svg.png';
import { resolveApiBaseList } from '../utils/apiBase';

// ===== Config dinámica (misma idea que en los otros archivos) =====
const DIAS = ['Lunes','Martes','Miércoles','Jueves','Viernes'];
const WINDOW_START = '07:00';
const WINDOW_END   = '15:00';
const RECESOS = [
  ['10:00','10:30'],
  ['12:30','12:45'],
];

const hhmm = (t) => (t || '').slice(0,5);
const overlaps = (aIni, aFin, bIni, bFin) => (aIni < bFin) && (aFin > bIni);

// Construye filas dinámicas (renglones) a partir de ventana + recesos + horarios (HH:MM)
function buildFilas(horarios = []) {
  const cuts = new Set([WINDOW_START, WINDOW_END]);
  // cortes por recesos
  for (const [ri, rf] of RECESOS) { cuts.add(ri); cuts.add(rf); }
  // cortes por clases (ya deben venir como HH:MM)
  horarios.forEach(h => {
    cuts.add(h.horaInicio);
    cuts.add(h.horaFin);
  });

  const sorted = Array.from(cuts).sort(); // HH:MM lexicográfico funciona
  const filas = [];
  for (let i = 0; i < sorted.length - 1; i++) {
    const ini = sorted[i], fin = sorted[i + 1];
    if (ini >= fin) continue;
    const esReceso = RECESOS.some(([ri, rf]) => overlaps(ini, fin, ri, rf));
    filas.push({ ini, fin, label: `${ini} - ${fin}`, receso: esReceso });
  }
  return filas;
}

// Helper: convertir imagen a dataURL para jsPDF.addImage
function imgToDataURL(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = reject;
    img.src = src;
  });
}

const ConsultarHorarios = () => {
  const [vista, setVista] = useState('grupo');

  // ---------- Estado para GRUPO ----------
  const [grupos, setGrupos] = useState([]);
  const [grupoId, setGrupoId] = useState('');
  const [nombreGrupo, setNombreGrupo] = useState('');
  const [cargando, setCargando] = useState(false);
  // plano dinámico: [{dia,horaInicio(HH:MM),horaFin(HH:MM),materia,aula}]
  const [horariosPlanoGrupo, setHorariosPlanoGrupo] = useState([]);

  const apiBases = useMemo(() => resolveApiBaseList(), []);

  const fetchJsonWithFallback = useCallback(async (path, options = {}) => {
    let lastError = null;
    for (const base of apiBases) {
      const normalizedBase = base.replace(/\/$/, '');
      const normalizedPath = path.startsWith('/') ? path : `/${path}`;
      const url = `${normalizedBase}${normalizedPath}`;
      try {
        const response = await fetch(url, options);
        const text = await response.text();

        if (!response.ok) {
          let message = text || `Error ${response.status}`;
          try {
            const parsed = text ? JSON.parse(text) : null;
            if (parsed && typeof parsed === 'object') {
              message = parsed.message || parsed.error || message;
            }
          } catch (parseErr) {
            // mantener mensaje original
          }
          const error = new Error(message);
          error.status = response.status;
          lastError = error;
          console.warn(`Warning: petición fallida a ${url}:`, error);
          continue;
        }

        const contentType = response.headers.get('content-type') || '';
        const trimmed = text.trim();
        if (!trimmed) return {};

        if (contentType.includes('application/json') || trimmed.startsWith('{') || trimmed.startsWith('[')) {
          return JSON.parse(trimmed);
        }

        try {
          return JSON.parse(trimmed);
        } catch (parseErr) {
          lastError = new Error(`Respuesta inválida desde ${url}`);
          console.warn(`Warning: contenido no JSON desde ${url} (content-type: ${contentType || 'desconocido'})`);
        }
      } catch (error) {
        lastError = error;
        console.warn(`Warning: error de red al solicitar ${url}:`, error);
      }
    }

    throw lastError || new Error('No se pudo contactar al servidor.');
  }, [apiBases]);

  useEffect(() => {
    const cargarGrupos = async () => {
      try {
        const data = await fetchJsonWithFallback('/horarios/consulta/grupos');
        setGrupos(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('No fue posible cargar los grupos:', error);
        setGrupos([]);
        alert('No fue posible cargar los grupos.');
      }
    };

    cargarGrupos();
  }, [fetchJsonWithFallback]);

  const consultarHorarioGrupo = async () => {
    if (!grupoId) {
      alert('Selecciona un grupo.');
      return;
    }
    setCargando(true);
    try {
      const g = grupos.find(x => String(x.IdGrupo) === String(grupoId));
      setNombreGrupo(
        g ? `${g.GradoTexto || `Grado ${g.Grado || ''}`}${g.CicloEscolar ? ` (${g.CicloEscolar})` : ''}` : ''
      );

      const rows = await fetchJsonWithFallback(`/horarios/consulta/por-grupo?grupoId=${encodeURIComponent(grupoId)}`);
      const adaptado = (rows || []).map(x => ({
        dia: x.DiaSemana,
        horaInicio: hhmm(x.HoraInicio),
        horaFin: hhmm(x.HoraFin),
        materia: x.Materia || '',
        aula: x.Aula || ''
      }));
      setHorariosPlanoGrupo(adaptado);
    } catch (e) {
      console.error(e);
      setHorariosPlanoGrupo([]);
      alert('No fue posible obtener el horario del grupo.');
    } finally {
      setCargando(false);
    }
  };

  // Filas dinámicas para GRUPO
  const FILAS_GRUPO = useMemo(() => buildFilas(horariosPlanoGrupo), [horariosPlanoGrupo]);

  // Exportaciones (tabla del GRUPO)
  const exportarExcelGrupo = () => {
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.table_to_sheet(document.getElementById('tablaHorariosGrupo'));
    XLSX.utils.book_append_sheet(wb, ws, 'Horarios');
    XLSX.writeFile(wb, 'horarios_grupo.xlsx');
  };
  async function exportarPDFGrupo() {
    const doc = new jsPDF('p', 'pt');
    const margenX = 40;

    try {
      const left = await imgToDataURL(logoInstituto);
      doc.addImage(left, 'PNG', margenX, 28, 52, 52);
    } catch {}

    // Encabezado
    doc.setFont('helvetica', 'bold'); doc.setFontSize(16);
    doc.text('Instituto Silviano Carrillo', doc.internal.pageSize.getWidth() / 2, 50, { align: 'center' });

    doc.setFont('helvetica', 'normal'); doc.setFontSize(11);
    doc.text('Consulta de Horarios por Grupo', doc.internal.pageSize.getWidth() / 2, 68, { align: 'center' });

    // Tarjeta (opcional, mínima)
    const head = [['Hora', ...DIAS]];
    const body = FILAS_GRUPO.map(f => {
      if (f.receso) {
        return [
          { content: f.label, styles: { fontStyle: 'bold' } },
          { content: 'RECESO', colSpan: DIAS.length, styles: { halign: 'center', fontStyle: 'bold', fillColor: [240,240,240] } }
        ];
      }
      const fila = [f.label];
      DIAS.forEach(d => {
        const c = horariosPlanoGrupo.find(h => h.dia === d && overlaps(f.ini, f.fin, h.horaInicio, h.horaFin));
        fila.push(c ? `${c.materia}${c.aula ? `\nAula ${c.aula}` : ''}` : '');
      });
      return fila;
    });

    autoTable(doc, {
      head, body,
      startY: 120,
      styles: { halign: 'center', valign: 'middle', fontSize: 9, cellPadding: 4 },
      headStyles: { fillColor: [230,230,230] },
      columnStyles: { 0: { cellWidth: 100, fontStyle: 'bold' } },
      theme: 'grid',
      margin: { left: margenX, right: margenX },
    });

    doc.save(`horario_grupo_${(nombreGrupo || 'grupo').replace(/\s+/g,'_')}.pdf`);
  }

  // ---------- Estado para DOCENTE ----------
  const [docentes, setDocentes] = useState([]);
  const [docenteId, setDocenteId] = useState('');
  const [nombreDocente, setNombreDocente] = useState('');
  const [gruposDoc, setGruposDoc] = useState([]);
  const [grupoDocId, setGrupoDocId] = useState('');
  const [horariosPlanoDoc, setHorariosPlanoDoc] = useState([]);
  const [cargandoDoc, setCargandoDoc] = useState(false);

  // Filas dinámicas para DOCENTE
  const FILAS_DOC = useMemo(() => buildFilas(horariosPlanoDoc), [horariosPlanoDoc]);

  // Cargar lista de docentes (los que tienen horario)
  useEffect(() => {
    if (vista !== 'docente') return;
    (async () => {
      try {
        const rows = await fetchJsonWithFallback('/horarios/consulta/docentes');
        setDocentes(Array.isArray(rows) ? rows : []);
      } catch (error) {
        console.error('No fue posible cargar los docentes:', error);
        setDocentes([]);
        alert('No fue posible cargar los docentes.');
      }
    })();
  }, [fetchJsonWithFallback, vista]);

  // Al elegir docente, cargar sus grupos
  const onSelectDocente = async (id) => {
    setDocenteId(id);
    setNombreDocente('');
    setGruposDoc([]);
    setGrupoDocId('');
    setHorariosPlanoDoc([]);

    if (!id) return;

    const d = docentes.find(x => String(x.IdDocente) === String(id));
    setNombreDocente(d?.NombreCompleto || '');

    try {
      const rows = await fetchJsonWithFallback(`/horarios/consulta/grupos-por-docente?docenteId=${encodeURIComponent(id)}`);
      setGruposDoc(Array.isArray(rows) ? rows : []);
    } catch (error) {
      console.error('No fue posible cargar los grupos del docente:', error);
      setGruposDoc([]);
      alert('No fue posible cargar los grupos del docente.');
    }
  };

  // Consultar horario del docente en el grupo elegido
  const consultarHorarioDocente = async () => {
    if (!docenteId || !grupoDocId) {
      alert('Selecciona docente y grupo.');
      return;
    }
    setCargandoDoc(true);
    try {
      const rows = await fetchJsonWithFallback(
        `/horarios/consulta/horario-docente?docenteId=${encodeURIComponent(docenteId)}&grupoId=${encodeURIComponent(grupoDocId)}`
      );

      const adaptado = (rows || []).map(x => ({
        dia: x.DiaSemana,
        horaInicio: hhmm(x.HoraInicio),
        horaFin: hhmm(x.HoraFin),
        materia: x.Materia || '',
        aula: x.Aula || ''
      }));
      setHorariosPlanoDoc(adaptado);
    } catch (e) {
      console.error(e);
      setHorariosPlanoDoc([]);
      alert('No fue posible obtener el horario del docente.');
    } finally {
      setCargandoDoc(false);
    }
  };

  // Exportaciones (tabla del DOCENTE)
  const exportarExcelDocente = () => {
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.table_to_sheet(document.getElementById('tablaHorariosDocente'));
    XLSX.utils.book_append_sheet(wb, ws, 'Horario Docente');
    XLSX.writeFile(wb, `horario_docente.xlsx`);
  };
  async function exportarPDFDocente() {
    const doc = new jsPDF('p', 'pt');
    const margenX = 40;

    try {
      const left = await imgToDataURL(logoInstituto);
      doc.addImage(left, 'PNG', margenX, 28, 52, 52);
    } catch {}

    doc.setFont('helvetica', 'bold'); doc.setFontSize(16);
    doc.text('Instituto Silviano Carrillo', doc.internal.pageSize.getWidth() / 2, 50, { align: 'center' });

    doc.setFont('helvetica', 'normal'); doc.setFontSize(11);
    doc.text('Consulta de Horarios del Docente', doc.internal.pageSize.getWidth() / 2, 68, { align: 'center' });

    const head = [['Hora', ...DIAS]];
    const body = FILAS_DOC.map(f => {
      if (f.receso) {
        return [
          { content: f.label, styles: { fontStyle: 'bold' } },
          { content: 'RECESO', colSpan: DIAS.length, styles: { halign: 'center', fontStyle: 'bold', fillColor: [240,240,240] } }
        ];
      }
      const fila = [f.label];
      DIAS.forEach(d => {
        const c = horariosPlanoDoc.find(h => h.dia === d && overlaps(f.ini, f.fin, h.horaInicio, h.horaFin));
        fila.push(c ? `${c.materia}${c.aula ? `\nAula ${c.aula}` : ''}` : '');
      });
      return fila;
    });

    autoTable(doc, {
      head, body,
      startY: 120,
      styles: { halign: 'center', valign: 'middle', fontSize: 9, cellPadding: 4 },
      headStyles: { fillColor: [230,230,230] },
      columnStyles: { 0: { cellWidth: 100, fontStyle: 'bold' } },
      theme: 'grid',
      margin: { left: margenX, right: margenX },
    });

    doc.save(`horario_${(nombreDocente || 'docente').replace(/\s+/g,'_')}.pdf`);
  }

  // ---------- VISTA ----------
  return (
    <div className="container py-5">
      {/* Encabezado */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <img src={logoInstituto} alt="Logo Instituto" style={{ height: 60 }} />
        <h4 className="text-center flex-grow-1 mb-0">
          Instituto Silviano Carrillo<br />
          <small className="text-muted">Consultar Horarios</small>
        </h4>
        <img src={logoSEP} alt="Logo SEP" style={{ height: 60 }} />
      </div>

      {/* Botones de selección */}
      <div className="mb-4 d-flex justify-content-center gap-4">
        <button className={`btn ${vista==='grupo'?'btn-primary':'btn-outline-primary'}`} onClick={() => setVista('grupo')}>
          Consultar por Grupo
        </button>
        <button className={`btn ${vista==='docente'?'btn-success':'btn-outline-success'}`} onClick={() => setVista('docente')}>
          Consultar por Docente
        </button>
      </div>

      {/* ---------- Consulta por Grupo ---------- */}
      {vista === 'grupo' && (
        <div className="container">
          <h2 className="text-center mb-3">Consulta de Horarios por Grupo</h2>

          <div className="row g-3 mb-3">
            <div className="col-md-8">
              <select
                className="form-select"
                value={grupoId}
                onChange={(e) => setGrupoId(e.target.value)}
                disabled={grupos.length === 0 || cargando}
              >
                <option value="">Selecciona un grupo...</option>
                {grupos.map(g => (
                  <option key={g.IdGrupo} value={g.IdGrupo}>
                    {g.GradoTexto || `Grado ${g.Grado}`} {g.CicloEscolar ? `(${g.CicloEscolar})` : ''}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-4">
              <button
                type="button"
                className="btn btn-primary w-100"
                onClick={consultarHorarioGrupo}
                disabled={!grupoId || cargando}
              >
                {cargando ? 'Cargando…' : 'Consultar Horario'}
              </button>
            </div>
          </div>

          {FILAS_GRUPO.length > 0 && (
            <>
              <h5 className="text-center">Horario del Grupo <span>{nombreGrupo}</span></h5>

              <table id="tablaHorariosGrupo" className="table table-bordered text-center align-middle">
                <thead>
                  <tr>
                    <th style={{width:'120px'}}>Hora</th>
                    {DIAS.map(d => <th key={d}>{d}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {FILAS_GRUPO.map((f) => (
                    <tr key={f.label}>
                      <td className="fw-semibold">{f.label}</td>
                      {f.receso ? (
                        <td colSpan={DIAS.length} className="table-secondary fw-bold">Receso</td>
                      ) : (
                        DIAS.map((d) => {
                          const c = horariosPlanoGrupo.find(h =>
                            h.dia === d && overlaps(f.ini, f.fin, h.horaInicio, h.horaFin)
                          );
                          return (
                            <td key={`${f.label}-${d}`}>
                              {c && (
                                <>
                                  <div className="fw-semibold">{c.materia}</div>
                                  {c.aula ? <small className="text-muted">Aula {c.aula}</small> : null}
                                </>
                              )}
                            </td>
                          );
                        })
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="d-flex justify-content-end gap-2">
                <button className="btn btn-outline-success" onClick={exportarExcelGrupo}>Exportar a Excel</button>
                <button className="btn btn-outline-danger" onClick={exportarPDFGrupo}>Exportar a PDF</button>
              </div>
            </>
          )}
        </div>
      )}

      {/* ---------- Consulta por Docente ---------- */}
      {vista === 'docente' && (
        <div className="container">
          <h2 className="text-center mb-3">Consulta de Horarios por Docente</h2>

          <div className="row g-3 mb-3">
            <div className="col-md-6">
              <label className="form-label">Docente</label>
              <select
                className="form-select"
                value={docenteId}
                onChange={(e) => onSelectDocente(e.target.value)}
              >
                <option value="">Selecciona un docente…</option>
                {docentes.map(d => (
                  <option key={d.IdDocente} value={d.IdDocente}>{d.NombreCompleto}</option>
                ))}
              </select>
            </div>

            <div className="col-md-6">
              <label className="form-label">Grupo del docente</label>
              <select
                className="form-select"
                value={grupoDocId}
                onChange={(e) => setGrupoDocId(e.target.value)}
                disabled={!docenteId || gruposDoc.length === 0}
              >
                <option value="">Selecciona un grupo…</option>
                {gruposDoc.map(g => (
                  <option key={g.IdGrupo} value={g.IdGrupo}>
                    {`Grupo ${g.IdGrupo} — Grado ${g.Grado}${g.CicloEscolar ? ` (${g.CicloEscolar})` : ''}`}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-12">
              <button
                type="button"
                className="btn btn-success w-100"
                onClick={consultarHorarioDocente}
                disabled={!docenteId || !grupoDocId || cargandoDoc}
              >
                {cargandoDoc ? 'Cargando…' : 'Consultar Horario'}
              </button>
            </div>
          </div>

          {FILAS_DOC.length > 0 && (
            <>
              <h5 className="text-center">
                Horario de <span>{nombreDocente}</span> — Grupo <span>{grupoDocId}</span>
              </h5>

              <table id="tablaHorariosDocente" className="table table-bordered text-center align-middle">
                <thead>
                  <tr>
                    <th style={{width:'120px'}}>Hora</th>
                    {DIAS.map(d => <th key={d}>{d}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {FILAS_DOC.map((f) => (
                    <tr key={f.label}>
                      <td className="fw-semibold">{f.label}</td>
                      {f.receso ? (
                        <td colSpan={DIAS.length} className="table-secondary fw-bold">Receso</td>
                      ) : (
                        DIAS.map((d) => {
                          const c = horariosPlanoDoc.find(h =>
                            h.dia === d && overlaps(f.ini, f.fin, h.horaInicio, h.horaFin)
                          );
                          return (
                            <td key={`${f.label}-${d}`}>
                              {c && (
                                <>
                                  <div className="fw-semibold">{c.materia}</div>
                                  {c.aula ? <small className="text-muted">Aula {c.aula}</small> : null}
                                </>
                              )}
                            </td>
                          );
                        })
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="d-flex justify-content-end gap-2">
                <button className="btn btn-outline-success" onClick={exportarExcelDocente}>Exportar a Excel</button>
                <button className="btn btn-outline-danger" onClick={exportarPDFDocente}>Exportar a PDF</button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ConsultarHorarios;
